import { useCallback, useMemo, useState } from 'react';
import { DataTable, type TableCellRef } from '@/components/ui/data-table/data-table';
import { useTicketBreakdown } from '@/hooks/use-analytics-v2';
import type { TicketBreakdownLevel, TicketBreakdownRow } from '@/types/analyticsV2';
import { TicketDrilldown } from '../../drilldown/ticket-drilldown';
import { ticketDrilldownView, useTicketDrilldown } from '../../drilldown/ticket-drilldowns';
import { BreakdownBreadcrumb, type BreakdownPath } from './breakdown-breadcrumb';
import {
  BREAKDOWN_NUMBERS,
  breakdownColumns,
  breakdownRowId,
  breakdownRowName,
  canDrill,
  type OpenDrilldown,
} from './breakdown-columns';

const HINT: Record<TicketBreakdownLevel, string> = {
  category: 'Select a category to see its subcategories, or a number to see its tickets',
  subcategory: 'Select a subcategory to see its intents, or a number to see its tickets',
  intent: 'Select an intent or a number to see its tickets',
};

const levelOf = ({ category, subcategory }: BreakdownPath): TicketBreakdownLevel => {
  if (subcategory != null) return 'intent';
  return category != null ? 'subcategory' : 'category';
};

interface TicketBreakdownSectionProps {
  fromDate: string;
  toDate: string;
  /** Super-admin only: render metrics for this account instead of the caller's own. */
  overrideAccountId?: number;
}

/** The Ticket tab's breakdown: tickets by category, then a category's subcategories, then a subcategory's intents. */
export function TicketBreakdownSection({ fromDate, toDate, overrideAccountId }: TicketBreakdownSectionProps) {
  const [path, setPath] = useState<BreakdownPath>({});
  const { show, ...drilldown } = useTicketDrilldown();
  // The number whose tickets were opened last, with its range; it stays marked after the panel closes,
  // until another number opens, the level changes or the range does.
  const [opened, setOpened] = useState<{ cell: TableCellRef; range: string } | null>(null);
  const by = levelOf(path);
  const range = `${fromDate}..${toDate}`;
  const { data, isLoading, isError } = useTicketBreakdown(fromDate, toDate, { by, ...path }, overrideAccountId);

  const openTickets = useCallback<OpenDrilldown>(
    (row, column) => {
      const scope = [path.category, path.subcategory].filter((name): name is string => name != null);
      show(ticketDrilldownView(column, { ...path, [by]: row.key }, scope.concat(breakdownRowName(by, row))));
      setOpened({ cell: { rowId: breakdownRowId(row), columnId: column }, range });
    },
    [by, path, range, show],
  );
  const columns = useMemo(() => breakdownColumns(by, openTickets), [by, openTickets]);

  const navigate = (next: BreakdownPath) => {
    setPath(next);
    setOpened(null);
  };
  const open = (row: TicketBreakdownRow) => {
    if (by === 'intent') return openTickets(row, 'tickets');
    if (!canDrill(by, row)) return;
    navigate(by === 'category' ? { category: row.key! } : { ...path, subcategory: row.key! });
  };

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-baseline justify-between gap-3'>
        <h2 className='text-[12px] text-gray-800 font-medium'>Tickets by {by}</h2>
        <span className='text-[12px] text-gray-700'>{HINT[by]}</span>
      </div>
      <div className='rounded-3xl border border-gray-300 bg-white p-2'>
        {path.category != null ? <BreakdownBreadcrumb path={path} onNavigate={navigate} /> : null}
        <DataTable
          columns={columns}
          data={data ?? []}
          enableSorting={false}
          enablePagination={false}
          isLoading={isLoading}
          onRowClick={open}
          getRowId={breakdownRowId}
          crosshairColumnIds={BREAKDOWN_NUMBERS}
          activeCell={opened?.range === range ? opened.cell : null}
          getRowClassName={(row) => (by === 'intent' || canDrill(by, row) ? '' : 'cursor-default')}
          emptyMessage={isError ? 'Failed to load the breakdown.' : 'No tickets in this range.'}
        />
      </div>
      <TicketDrilldown {...drilldown} fromDate={fromDate} toDate={toDate} overrideAccountId={overrideAccountId} />
    </div>
  );
}
